// STAGE SUBSETS (firm model, 2026-06): a subset is an ordinary scope row that
// points at a parent row and INHERITS the parent's `stage`. Because the stage
// string is inherited, every group-by-stage summary and chart rolls subsets up
// into their parent automatically — no calculation anywhere had to change.
// One level only: a subset can never have subsets of its own.
export const isSubset = (s) => !!s?.parentScopeId;

export const subsetLabelOf = (s) => s?.subsetLabel || s?.stage || "—";

export const childIdxs = (scopes, parentId) =>
  (scopes || []).map((_, i) => i).filter((i) => scopes[i].parentScopeId === parentId);

// Display order for the grid: each parent followed by its own subsets.
// Sub-consultant rows are excluded (they live in their own section).
export const orderedIdxs = (scopes) => {
  const out = [];
  (scopes || []).forEach((s, i) => {
    if (s.isSubConsultant || isSubset(s)) return;
    out.push(i);
    out.push(...childIdxs(scopes, s.id));
  });
  // Orphaned subsets (parent deleted elsewhere) still need to be reachable.
  (scopes || []).forEach((s, i) => {
    if (s.isSubConsultant || !isSubset(s)) return;
    if (!out.includes(i)) out.push(i);
  });
  return out;
};

export const parentIdxs = (scopes) =>
  (scopes || []).map((_, i) => i).filter((i) => !scopes[i].isSubConsultant && !isSubset(scopes[i]));

// Reorder: swapping two SUBSET siblings is a plain swap; swapping two PARENTS
// moves each parent together with its own subsets, so a stage block never
// gets split apart.
export function reorderScopes(scopes, fromIdx, toIdx) {
  const list = scopes || [];
  if (toIdx == null || fromIdx === toIdx || !list[fromIdx] || !list[toIdx]) return list;
  if (isSubset(list[fromIdx])) {
    const next = [...list];
    [next[fromIdx], next[toIdx]] = [next[toIdx], next[fromIdx]];
    return next;
  }
  const parents = parentIdxs(list);
  const fromPos = parents.indexOf(fromIdx);
  const toPos = parents.indexOf(toIdx);
  if (fromPos < 0 || toPos < 0) return list;
  const order = [...parents];
  [order[fromPos], order[toPos]] = [order[toPos], order[fromPos]];
  const out = [];
  order.forEach((pi) => {
    out.push(list[pi]);
    childIdxs(list, list[pi].id).forEach((ci) => out.push(list[ci]));
  });
  list.forEach((s) => { if (!out.includes(s)) out.push(s); });
  return out;
}

export function newSubset(parent, months, existingChildren = 0) {  return {
    id: (globalThis.crypto?.randomUUID?.() || `ss_${Math.random().toString(36).slice(2, 10)}`),
    parentScopeId: parent.id,
    subsetLabel: `${parent.stage || "Stage"} — subset ${existingChildren + 1}`,
    stage: parent.stage || "",
    feeType: parent.feeType || "fixed",
    probability: parent.probability ?? 1.0,
    contract: parent.contract || "Tentative",
    contractValue: 0,
    note: "",
    revenue: Object.fromEntries((months || []).map((mo) => [mo, 0])),
    revenueRatios: {},
  };
}
